import {
    ResponsiveModal,
    ResponsiveModalBody,
    ResponsiveModalContent,
    ResponsiveModalFooter,
    ResponsiveModalHeader,
    ResponsiveModalTitle,
} from '@/components/ui/responsive-modal';
import { Button } from '@/components/ui/shadcn/button';
import { Textarea } from '@/components/ui/shadcn/textarea';
import type { PurchaseOrderItemCategory, PurchaseOrderItemForm } from '@/types/purchase-order';
import { AlertTriangle, MessageSquare, Trash2 } from 'lucide-react';
import { useCallback, useRef, useState } from 'react';

export type EditablePurchaseOrderItemForm = PurchaseOrderItemForm & {
    item_status?: string;
    qty_received?: number | string;
};

export function emptyItemRow(): EditablePurchaseOrderItemForm {
    return {
        description: '',
        qty_ordered: '',
        unit: 'pcs',
        unit_price: '',
        category_id: '',
        delivery_term: '',
        ab_number: '',
        item_status: '',
        notes: '',
    };
}

export function isRowEmpty(item: PurchaseOrderItemForm): boolean {
    return !item.description && !item.qty_ordered && !item.unit_price;
}

export function ensureTrailingRow(items: EditablePurchaseOrderItemForm[]): EditablePurchaseOrderItemForm[] {
    if (items.length === 0 || !isRowEmpty(items[items.length - 1])) {
        return [...items, emptyItemRow()];
    }
    return items;
}

export function itemsFromPO(
    rawItems: {
        id: string;
        description: string;
        qty_ordered: string | number;
        unit: string | null;
        unit_price: string | number;
        category_id: string | null;
        delivery_term: string | null;
        ab_number: string | null;
        notes: string | null;
        item_status?: string | null;
        qty_received?: number | string | null;
    }[],
): EditablePurchaseOrderItemForm[] {
    return rawItems.map((i) => ({
        id: i.id,
        description: i.description,
        qty_ordered: String(Number(i.qty_ordered)),
        unit: i.unit ?? '',
        unit_price: String(Number(i.unit_price)),
        category_id: i.category_id ?? '',
        delivery_term: i.delivery_term ?? '',
        ab_number: i.ab_number ?? '',
        item_status: i.item_status ?? '',
        notes: i.notes ?? '',
        qty_received: i.qty_received ?? 0,
    }));
}

export function serializeItems(items: EditablePurchaseOrderItemForm[]): PurchaseOrderItemForm[] {
    return items
        .filter((item) => item.description && item.qty_ordered && item.unit_price)
        .map((item) => ({
            id: item.id,
            description: item.description,
            qty_ordered: item.qty_ordered,
            unit: item.unit,
            unit_price: item.unit_price,
            category_id: item.category_id,
            delivery_term: item.delivery_term,
            ab_number: item.ab_number,
            item_status: item.item_status,
            notes: item.notes,
        }));
}

function calcLineTotal(item: PurchaseOrderItemForm): number {
    return (parseFloat(item.qty_ordered) || 0) * (parseFloat(item.unit_price) || 0);
}

function formatCurrency(amount: number | string | null | undefined, currency: string): string {
    const num = Number(amount ?? 0);
    return new Intl.NumberFormat('lt-LT', { style: 'currency', currency: currency || 'EUR' }).format(num);
}

const ITEM_STATUS_OPTIONS = ['', 'ordered', 'confirmed', 'received', 'cancelled'] as const;

interface ItemGridProps {
    items: EditablePurchaseOrderItemForm[];
    onChange: (items: EditablePurchaseOrderItemForm[]) => void;
    readOnly?: boolean;
    currency: string;
    categories: PurchaseOrderItemCategory[];
    canRemoveRows?: boolean;
    showStatus?: boolean;
    showQtyReceived?: boolean;
    showAddRowAction?: boolean;
    onStatusChange?: (itemId: string, status: string) => void;
    t: (key: string, fallback?: string) => string;
}

export function ItemGrid({
    items,
    onChange,
    readOnly = false,
    currency,
    categories,
    canRemoveRows = false,
    showStatus = false,
    showQtyReceived = false,
    showAddRowAction = true,
    onStatusChange,
    t,
}: ItemGridProps) {
    const gridRef = useRef<HTMLTableElement>(null);
    const [commentRow, setCommentRow] = useState<number | null>(null);
    const [commentDraft, setCommentDraft] = useState('');

    const update = useCallback(
        (index: number, field: keyof PurchaseOrderItemForm, value: string) => {
            const updated = items.map((item, i) => (i === index ? { ...item, [field]: value } : item));
            onChange(readOnly ? updated : ensureTrailingRow(updated));
        },
        [items, onChange, readOnly],
    );

    const handleRowFocus = useCallback(
        (rowIdx: number) => {
            if (!readOnly && rowIdx === items.length - 1) {
                onChange([...items, emptyItemRow()]);
            }
        },
        [items, onChange, readOnly],
    );

    const removeRow = (index: number) => {
        if (!canRemoveRows || items.length <= 2) return;
        onChange(items.filter((_, i) => i !== index));
    };

    // colIdx counts only the keyboard-navigable input columns
    const LAST_COL = 4; // desc=0, qty=1, unit=2, unit_price=3, delivery_term=4

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, rowIdx: number, colIdx: number) => {
        const moveToNextRow = () => {
            const rows = gridRef.current?.querySelectorAll('tbody tr');
            (rows?.[rowIdx + 1]?.querySelector('input') as HTMLInputElement)?.focus();
        };
        if (e.key === 'Tab' && !e.shiftKey && colIdx === LAST_COL) {
            e.preventDefault();
            moveToNextRow();
        }
        if (e.key === 'Enter') {
            e.preventDefault();
            moveToNextRow();
        }
    };

    const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
        if (readOnly) return;
        const text = e.clipboardData.getData('text/plain');
        if (!text.includes('\t')) return;
        e.preventDefault();
        const pasted: EditablePurchaseOrderItemForm[] = text
            .trim()
            .split('\n')
            .map((row) => {
                const cols = row.split('\t');
                return {
                    description: cols[0] ?? '',
                    qty_ordered: cols[1] ?? '',
                    unit: cols[2] ?? 'pcs',
                    unit_price: cols[3] ?? '',
                    category_id: '',
                    delivery_term: cols[4] ?? '',
                    ab_number: '',
                    item_status: '',
                    notes: '',
                };
            });
        onChange(ensureTrailingRow([...items.filter((item) => item.description || item.qty_ordered), ...pasted]));
    };

    const cell = 'w-full border-0 bg-transparent px-2 py-1.5 text-sm outline-none focus:bg-accent/50 focus:ring-0';
    const categoryOptions = [{ value: '__none__', label: '—' }, ...categories.map((c) => ({ value: c.id, label: c.name }))];

    return (
        <div onPaste={handlePaste}>
            <div className="overflow-x-auto rounded-lg border">
                <table ref={gridRef} className="w-full border-collapse text-sm">
                    <thead>
                        <tr className="bg-muted/50 text-muted-foreground border-b text-xs font-medium">
                            <th className="w-8 border-r px-2 py-2 text-center">#</th>
                            <th className="min-w-60 border-r px-2 py-2 text-left">
                                {t('purchase_orders.show.line_items.columns.description', 'Description')}
                            </th>
                            <th className="w-28 border-r px-2 py-2 text-left">{t('purchase_orders.form.columns.category', 'Category')}</th>
                            <th className="w-20 border-r px-2 py-2 text-right">{t('purchase_orders.show.line_items.columns.qty_ordered', 'Qty')}</th>
                            {showQtyReceived && (
                                <th className="w-24 border-r px-2 py-2 text-right">
                                    {t('purchase_orders.show.line_items.columns.qty_received', 'Received')}
                                </th>
                            )}
                            <th className="w-14 border-r px-2 py-2 text-left">{t('purchase_orders.show.line_items.columns.unit', 'Unit')}</th>
                            <th className="w-28 border-r px-2 py-2 text-right">
                                {t('purchase_orders.show.line_items.columns.unit_price', 'Unit Price')}
                            </th>
                            <th className="w-26 border-r px-2 py-2 text-left">{t('purchase_orders.form.columns.delivery_term', 'Delivery')}</th>
                            <th className="w-24 border-r px-2 py-2 text-left">{t('purchase_orders.form.columns.ab_number', 'AB №')}</th>
                            <th className="w-28 border-r px-2 py-2 text-right">{t('purchase_orders.show.line_items.columns.line_total', 'Total')}</th>
                            {showStatus && (
                                <th className="w-32 border-r px-2 py-2 text-left">{t('purchase_orders.show.line_items.columns.status', 'Status')}</th>
                            )}
                            <th className="w-8 border-r" />
                            {!readOnly && <th className="w-8" />}
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((item, rowIdx) => {
                            const categoryLabel = categories.find((c) => c.id === item.category_id)?.name ?? '—';
                            const isEmptyDraft = !readOnly && isRowEmpty(item);
                            const qtyOrdered = Number(item.qty_ordered) || 0;
                            const qtyReceived = Number(item.qty_received ?? 0);
                            const hasReceipt = qtyReceived > 0;
                            const isPartial = hasReceipt && qtyReceived < qtyOrdered;

                            return (
                                <tr
                                    key={rowIdx}
                                    className={`group border-b last:border-0 ${isPartial ? 'bg-warning-subtle hover:bg-warning-subtle' : 'hover:bg-muted/20'}`}
                                    onFocus={() => handleRowFocus(rowIdx)}
                                >
                                    <td className="text-muted-foreground border-r text-center text-xs">{rowIdx + 1}</td>

                                    {/* Description */}
                                    <td className="border-r p-0 align-top">
                                        {readOnly ? (
                                            <div className="px-2 py-2">
                                                <p className="font-medium">{item.description || '—'}</p>
                                                {item.notes && <p className="text-muted-foreground mt-1 text-xs">{item.notes}</p>}
                                            </div>
                                        ) : (
                                            <input
                                                className={cell}
                                                value={item.description}
                                                onChange={(e) => update(rowIdx, 'description', e.target.value)}
                                                onKeyDown={(e) => handleKeyDown(e, rowIdx, 0)}
                                                placeholder="Item description..."
                                            />
                                        )}
                                    </td>

                                    {/* Category */}
                                    <td className="border-r p-0 align-top">
                                        {readOnly ? (
                                            <span className="block px-2 py-2">{categoryLabel}</span>
                                        ) : (
                                            <select
                                                className={`${cell} cursor-pointer`}
                                                value={item.category_id || '__none__'}
                                                onChange={(e) => update(rowIdx, 'category_id', e.target.value === '__none__' ? '' : e.target.value)}
                                            >
                                                {categoryOptions.map((o) => (
                                                    <option key={o.value} value={o.value}>
                                                        {o.label}
                                                    </option>
                                                ))}
                                            </select>
                                        )}
                                    </td>

                                    {/* Qty */}
                                    <td className="border-r p-0 align-top">
                                        {readOnly ? (
                                            <span className="block px-2 py-2 text-right">{item.qty_ordered}</span>
                                        ) : (
                                            <input
                                                className={`${cell} text-right`}
                                                value={item.qty_ordered}
                                                onChange={(e) => update(rowIdx, 'qty_ordered', e.target.value)}
                                                onKeyDown={(e) => handleKeyDown(e, rowIdx, 1)}
                                                type="number"
                                                step="any"
                                                min="0"
                                                placeholder="0"
                                            />
                                        )}
                                    </td>

                                    {/* Received qty */}
                                    {showQtyReceived && (
                                        <td className="border-r px-2 py-2 text-right align-top">
                                            <span
                                                className={`inline-flex items-center gap-1 tabular-nums ${isPartial ? 'text-warning-subtle-foreground font-medium' : ''}`}
                                            >
                                                {isPartial && <AlertTriangle className="h-3 w-3 shrink-0" />}
                                                {qtyReceived}/{qtyOrdered}
                                            </span>
                                        </td>
                                    )}

                                    {/* Unit */}
                                    <td className="border-r p-0 align-top">
                                        {readOnly ? (
                                            <span className="block px-2 py-2">{item.unit || '—'}</span>
                                        ) : (
                                            <input
                                                className={cell}
                                                value={item.unit}
                                                onChange={(e) => update(rowIdx, 'unit', e.target.value)}
                                                onKeyDown={(e) => handleKeyDown(e, rowIdx, 2)}
                                                placeholder="pcs"
                                            />
                                        )}
                                    </td>

                                    {/* Unit Price */}
                                    <td className="border-r p-0 align-top">
                                        {readOnly ? (
                                            <span className="block px-2 py-2 text-right">{formatCurrency(item.unit_price, currency)}</span>
                                        ) : (
                                            <input
                                                className={`${cell} text-right`}
                                                value={item.unit_price}
                                                onChange={(e) => update(rowIdx, 'unit_price', e.target.value)}
                                                onKeyDown={(e) => handleKeyDown(e, rowIdx, 3)}
                                                type="number"
                                                step="any"
                                                min="0"
                                                placeholder="0.00"
                                            />
                                        )}
                                    </td>

                                    {/* Delivery term */}
                                    <td className="border-r p-0 align-top">
                                        {readOnly ? (
                                            <span className="block px-2 py-2">{item.delivery_term || '—'}</span>
                                        ) : (
                                            <input
                                                className={cell}
                                                value={item.delivery_term}
                                                onChange={(e) => update(rowIdx, 'delivery_term', e.target.value)}
                                                onKeyDown={(e) => handleKeyDown(e, rowIdx, 4)}
                                                placeholder="e.g. 2026-06-01"
                                            />
                                        )}
                                    </td>

                                    {/* AB № */}
                                    <td className="border-r p-0 align-top">
                                        {readOnly ? (
                                            <span className="block px-2 py-2">{item.ab_number || '—'}</span>
                                        ) : (
                                            <input
                                                className={cell}
                                                value={item.ab_number}
                                                onChange={(e) => update(rowIdx, 'ab_number', e.target.value)}
                                                placeholder="—"
                                            />
                                        )}
                                    </td>

                                    {/* Line total */}
                                    <td className="border-r px-2 py-2 text-right align-top font-medium tabular-nums">
                                        {isEmptyDraft ? '—' : formatCurrency(calcLineTotal(item), currency)}
                                    </td>

                                    {/* Status — always editable */}
                                    {showStatus && (
                                        <td className="border-r p-0 align-top">
                                            <select
                                                className={`${cell} cursor-pointer`}
                                                value={item.item_status || ''}
                                                onChange={(e) => {
                                                    if (readOnly && onStatusChange && item.id) {
                                                        onStatusChange(item.id, e.target.value);
                                                    } else {
                                                        update(rowIdx, 'item_status', e.target.value);
                                                    }
                                                }}
                                            >
                                                {ITEM_STATUS_OPTIONS.map((s) => (
                                                    <option key={s} value={s}>
                                                        {s === '' ? '—' : t(`purchase_orders.item_statuses.${s}`, s)}
                                                    </option>
                                                ))}
                                            </select>
                                        </td>
                                    )}

                                    {/* Comment */}
                                    <td className="w-8 border-r text-center align-top">
                                        {readOnly ? (
                                            item.notes ? (
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setCommentRow(rowIdx);
                                                        setCommentDraft(item.notes ?? '');
                                                    }}
                                                    className="text-primary p-1"
                                                    title={item.notes}
                                                >
                                                    <MessageSquare className="h-3.5 w-3.5" />
                                                </button>
                                            ) : null
                                        ) : (
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setCommentRow(rowIdx);
                                                    setCommentDraft(item.notes ?? '');
                                                }}
                                                className={`relative p-1 transition-opacity ${item.notes ? 'text-primary opacity-100' : 'text-muted-foreground opacity-0 group-hover:opacity-100'}`}
                                                title={item.notes ? 'Edit comment' : 'Add comment'}
                                            >
                                                <MessageSquare className="h-3.5 w-3.5" />
                                                {item.notes && <span className="bg-primary absolute top-0.5 right-0.5 h-1.5 w-1.5 rounded-full" />}
                                            </button>
                                        )}
                                    </td>

                                    {/* Delete */}
                                    {!readOnly && (
                                        <td className="w-8 text-center align-top">
                                            <button
                                                type="button"
                                                onClick={() => removeRow(rowIdx)}
                                                disabled={!canRemoveRows || items.length <= 2}
                                                className="text-muted-foreground hover:text-destructive p-1 opacity-0 transition-opacity group-hover:opacity-100 disabled:pointer-events-none"
                                            >
                                                <Trash2 className="h-3.5 w-3.5" />
                                            </button>
                                        </td>
                                    )}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {!readOnly && (
                <div className="text-muted-foreground mt-2 flex items-center gap-4 text-xs">
                    {showAddRowAction && (
                        <button
                            type="button"
                            onClick={() => onChange([...items, emptyItemRow()])}
                            className="text-primary hover:text-primary/80 text-sm font-medium"
                        >
                            {t('purchase_orders.form.add_item', '+ Add row')}
                        </button>
                    )}
                    <span>{t('purchase_orders.form.paste_hint', 'Paste from Excel — columns: Description, Qty, Unit, Unit Price, Delivery')}</span>
                </div>
            )}

            <ResponsiveModal
                open={commentRow !== null}
                onOpenChange={(open) => {
                    if (!open) setCommentRow(null);
                }}
                variant="sheet"
            >
                <ResponsiveModalContent size="md">
                    <ResponsiveModalHeader>
                        <ResponsiveModalTitle>{t('purchase_orders.form.comment_title', 'Line Item Comment')}</ResponsiveModalTitle>
                    </ResponsiveModalHeader>
                    <ResponsiveModalBody className="space-y-2">
                        {commentRow !== null && (
                            <p className="text-muted-foreground truncate text-sm">
                                {items[commentRow]?.description || `${t('purchase_orders.form.comment_unnamed_row', 'Row')} ${commentRow + 1}`}
                            </p>
                        )}
                        <Textarea
                            value={commentDraft}
                            onChange={(e) => setCommentDraft(e.target.value)}
                            placeholder={t('purchase_orders.form.comment_placeholder', 'Additional details, specifications, or remarks...')}
                            rows={4}
                        />
                    </ResponsiveModalBody>
                    <ResponsiveModalFooter>
                        <Button variant="outline" onClick={() => setCommentRow(null)}>
                            {t('common.cancel', 'Cancel')}
                        </Button>
                        <Button
                            onClick={() => {
                                if (commentRow !== null) update(commentRow, 'notes', commentDraft);
                                setCommentRow(null);
                            }}
                        >
                            {t('common.save', 'Save')}
                        </Button>
                    </ResponsiveModalFooter>
                </ResponsiveModalContent>
            </ResponsiveModal>
        </div>
    );
}
